/**
 * Absencja chorobowa (Etap 11) — enumy, kody ZUS i kontrakty API.
 *
 * Rozdział, który utrwala ten plik, jest rozdziałem z przepisów, nie wygodą
 * implementacji:
 *  - **wynagrodzenie chorobowe** (art. 92 Kodeksu pracy) płaci PRACODAWCA
 *    za pierwsze 33 dni niezdolności w roku kalendarzowym (14 dni, gdy
 *    pracownik ukończył 50 lat przed tym rokiem),
 *  - **zasiłek chorobowy** (ustawa z 25.06.1999 o świadczeniach pieniężnych
 *    z ubezpieczenia społecznego w razie choroby i macierzyństwa) należy się
 *    od dnia 34. (15.) i wypłaca go ZUS albo — gdy płatnik zgłasza więcej niż
 *    20 ubezpieczonych (art. 61) — sam płatnik,
 *  - **zasiłek z ubezpieczenia wypadkowego** za wypadek przy pracy idzie od
 *    PIERWSZEGO dnia w wysokości 100 % i nie zużywa puli z art. 92,
 *  - dzień bez prawa do świadczenia (okres wyczekiwania z art. 4) to
 *    usprawiedliwiona nieobecność bez wynagrodzenia — też trafia do ZUS-u,
 *    tylko z innym kodem.
 *
 * Kody świadczeń i przerw pochodzą z oficjalnego wykazu ZUS („Ogólne zasady
 * wypełniania i korygowania dokumentów ubezpieczeniowych", rozdział VII) —
 * patrz ADR-0012.
 */

/** Przyczyna niezdolności ze zwolnienia lekarskiego — decyduje o stawce i kodzie. */
export const SICK_LEAVE_REASONS = [
  /** Choroba albo odosobnienie w związku z chorobą zakaźną — 80 %. */
  'choroba',
  /** Wypadek w drodze do pracy lub z pracy — 100 % (art. 92 § 1 pkt 2 k.p.). */
  'wypadek_w_drodze',
  /** Choroba przypadająca w czasie ciąży — 100 % (art. 92 § 1 pkt 2 k.p.). */
  'ciaza',
  /** Badania dla kandydatów na dawców i zabieg pobrania — 100 % (art. 92 § 1 pkt 3). */
  'dawca',
  /**
   * Wypadek przy pracy albo choroba zawodowa — zasiłek z ubezpieczenia
   * wypadkowego 100 % od pierwszego dnia (art. 8 ust. 2 ustawy wypadkowej);
   * pula wynagrodzenia chorobowego z art. 92 k.p. się nie zmniejsza.
   */
  'wypadek_przy_pracy',
  /**
   * Gruźlica (kod literowy „D" na zaświadczeniu) — stawka jak przy chorobie,
   * czyli 80 %, ale okres zasiłkowy wydłuża się do 270 dni (art. 8 ustawy
   * zasiłkowej, pkt 43 komentarza ZUS).
   */
  'gruzlica',
] as const
export type SickLeaveReason = (typeof SICK_LEAVE_REASONS)[number]

/** Do czego uprawnia dany dzień niezdolności — wynik podziału, nie wybór kadr. */
export const SICK_ABSENCE_KINDS = [
  'wynagrodzenie_chorobowe',
  'zasilek_chorobowy',
  'zasilek_wypadkowy',
  /** Opieka nad dzieckiem albo innym członkiem rodziny — 80 % (Etap 21). */
  'zasilek_opiekunczy',
  /** Urlop macierzyński, rodzicielski albo ojcowski — stawka z rodzaju urlopu. */
  'zasilek_macierzynski',
  /**
   * Dni niezdolności PO wyczerpaniu okresu zasiłkowego (art. 8). Świadczenia
   * już nie ma; dalej idzie świadczenie rehabilitacyjne albo renta, ale o tym
   * decyduje ZUS, nie płatnik — dlatego liczymy zero i mówimy o tym wprost.
   */
  'po_okresie_zasilkowym',
  'bez_prawa',
] as const
export type SickAbsenceKind = (typeof SICK_ABSENCE_KINDS)[number]

/**
 * Kod świadczenia/przerwy do raportu ZUS RSA (oficjalny wykaz ZUS, rozdział VII):
 *  - 331 wynagrodzenie za czas niezdolności do pracy z powodu choroby,
 *        finansowane ze środków pracodawcy,
 *  - 313 zasiłek chorobowy z ubezpieczenia chorobowego,
 *  - 314 zasiłek chorobowy z ubezpieczenia wypadkowego,
 *  - 151 okres usprawiedliwionej nieobecności w pracy, bez prawa do
 *        wynagrodzenia lub zasiłku.
 */
export const ZUS_ABSENCE_CODES: Readonly<Record<SickAbsenceKind, string>> = {
  wynagrodzenie_chorobowe: '331',
  zasilek_chorobowy: '313',
  zasilek_wypadkowy: '314',
  zasilek_opiekunczy: '312',
  /* Macierzyński ma trzy kody — właściwy wybiera `zusAbsenceCode()`. */
  zasilek_macierzynski: '311',
  po_okresie_zasilkowym: '151',
  bez_prawa: '151',
}

// ---------------------------------------------------------------------------
// Zasiłek opiekuńczy (Etap 21)
// ---------------------------------------------------------------------------

/**
 * Nad kim sprawowana jest opieka — od tego zależy limit roczny, nie stawka
 * (pkt 257 komentarza ZUS do ustawy zasiłkowej):
 *  - dzieci do 14 lat (także niepełnosprawne w tym wieku) — 60 dni w roku,
 *  - chore dziecko niepełnosprawne 14–18 lat (albo niepełnosprawne 8–18 lat
 *    w sytuacji z art. 32 ust. 1 pkt 2a) — 30 dni,
 *  - inni członkowie rodziny, w tym chore dzieci powyżej 14 lat — 14 dni.
 */
export const CARE_SCOPES = [
  'dziecko_do_14',
  'dziecko_niepelnosprawne',
  'inny_czlonek_rodziny',
] as const
export type CareScope = (typeof CARE_SCOPES)[number]

export const CARE_LIMIT_DAYS: Readonly<Record<CareScope, number>> = {
  dziecko_do_14: 60,
  dziecko_niepelnosprawne: 30,
  inny_czlonek_rodziny: 14,
}

/** Limit wspólny, gdy w roku była opieka nad dzieckiem do 14 lat (pkt 257). */
export const CARE_JOINT_LIMIT_DAYS = 60

/**
 * Limit wspólny, gdy opieka dotyczyła tylko dziecka niepełnosprawnego powyżej
 * 14 lat i innych członków rodziny (pkt 258): łącznie 30 dni, w tym najwyżej
 * 14 dni na innych członków rodziny.
 */
export const CARE_JOINT_LIMIT_WITHOUT_YOUNG_CHILD_DAYS = 30

/**
 * Ile dni zasiłku opiekuńczego jeszcze przysługuje na dany tytuł. Limity
 * NIE sumują się swobodnie: obok limitu na tytuł obowiązuje limit wspólny
 * (60 dni, a bez opieki nad dzieckiem do 14 lat — 30 dni). Wszystkie trzy
 * przykłady z pkt 257–258 komentarza ZUS wychodzą z tego wzoru.
 */
export function careDaysAvailable(
  usedByScope: Readonly<Partial<Record<CareScope, number>>>,
  scope: CareScope,
): number {
  const used = (name: CareScope): number => usedByScope[name] ?? 0
  const totalUsed = CARE_SCOPES.reduce((sum, name) => sum + used(name), 0)
  const individual = CARE_LIMIT_DAYS[scope] - used(scope)
  const youngChildInvolved = used('dziecko_do_14') > 0 || scope === 'dziecko_do_14'
  const joint =
    (youngChildInvolved ? CARE_JOINT_LIMIT_DAYS : CARE_JOINT_LIMIT_WITHOUT_YOUNG_CHILD_DAYS) -
    totalUsed
  return Math.max(0, Math.min(individual, joint))
}

// ---------------------------------------------------------------------------
// Zasiłek macierzyński (Etap 21)
// ---------------------------------------------------------------------------

/** Rodzaj urlopu, za który idzie zasiłek — decyduje o stawce i o kodzie ZUS. */
export const MATERNITY_LEAVE_KINDS = ['macierzynski', 'rodzicielski', 'ojcowski'] as const
export type MaternityLeaveKind = (typeof MATERNITY_LEAVE_KINDS)[number]

/**
 * Stawki z pkt 168–170 komentarza ZUS: urlop macierzyński i ojcowski 100 %,
 * urlop rodzicielski 70 %.
 */
export const MATERNITY_RATE_BP: Readonly<Record<MaternityLeaveKind, number>> = {
  macierzynski: 10_000,
  rodzicielski: 7_000,
  ojcowski: 10_000,
}

/**
 * 81,5 % za CAŁY okres macierzyńskiego i rodzicielskiego — gdy pracownica
 * złożyła wniosek o oba urlopy w pełnym wymiarze nie później niż 21 dni po
 * porodzie (pkt 169). To fakt z wniosku, nie wybór systemu: dlatego zapisujemy
 * go przy absencji, a stawkę z niego wyprowadzamy.
 */
export const MATERNITY_COMBINED_RATE_BP = 8_150

/** Kody świadczenia w raporcie ZUS RSA dla poszczególnych urlopów. */
export const MATERNITY_ZUS_CODES: Readonly<Record<MaternityLeaveKind, string>> = {
  macierzynski: '311',
  rodzicielski: '319',
  ojcowski: '327',
}

/** Stawka zasiłku macierzyńskiego: z rodzaju urlopu albo 81,5 % z wniosku. */
export function maternityRateBp(kind: MaternityLeaveKind, combinedRequest: boolean): number {
  return combinedRequest ? MATERNITY_COMBINED_RATE_BP : MATERNITY_RATE_BP[kind]
}

/** Kod świadczenia do raportu ZUS RSA; macierzyński ma go per rodzaj urlopu. */
export function zusAbsenceCode(
  kind: SickAbsenceKind,
  maternityKind: MaternityLeaveKind | null = null,
): string {
  if (kind === 'zasilek_macierzynski') {
    return MATERNITY_ZUS_CODES[maternityKind ?? 'macierzynski']
  }
  return ZUS_ABSENCE_CODES[kind]
}

// ---------------------------------------------------------------------------
// Rodzaj świadczenia i okres zasiłkowy (Etap 21)
// ---------------------------------------------------------------------------

/**
 * Z czego wynika absencja w rejestrze. Rozdział jest potrzebny, bo każdy rodzaj
 * ma inną pulę: chorobowe zużywa okres zasiłkowy (182/270 dni), opiekuńcze —
 * limit roczny (60/30/14 dni), a macierzyńskie nie zużywa żadnego z nich.
 */
export const ABSENCE_BENEFIT_TYPES = ['chorobowe', 'opiekuncze', 'macierzynskie'] as const
export type AbsenceBenefitType = (typeof ABSENCE_BENEFIT_TYPES)[number]

/** Okres zasiłkowy: 182 dni, a przy gruźlicy i ciąży 270 (art. 8, pkt 43). */
export const BENEFIT_PERIOD_DAYS = 182
export const BENEFIT_PERIOD_LONG_DAYS = 270

/**
 * Przerwa, po której niezdolności NIE zlicza się do jednego okresu zasiłkowego
 * (art. 9 ust. 2, pkt 47). Od 2022 r. przyczyna niezdolności przed i po przerwie
 * nie ma znaczenia — liczy się tylko długość przerwy.
 */
export const BENEFIT_PERIOD_CHAIN_BREAK_DAYS = 60

/** Czy przyczyna wydłuża okres zasiłkowy do 270 dni (gruźlica albo ciąża). */
export function extendsBenefitPeriod(reason: SickLeaveReason): boolean {
  return reason === 'gruzlica' || reason === 'ciaza'
}

/** Skąd wzięła się podstawa wymiaru — pokazujemy to wprost, bo decyduje o kwocie. */
export const SICK_BASE_KINDS = [
  /** Przeciętne wynagrodzenie z 12 miesięcy kalendarzowych (art. 36 ust. 1). */
  'historia_12m',
  /** Krótsze zatrudnienie: pełne miesiące kalendarzowe ubezpieczenia (art. 36 ust. 2). */
  'pelne_miesiace',
  /** Niezdolność w pierwszym miesiącu: wynagrodzenie z umowy (art. 37). */
  'umowa_pierwszy_miesiac',
  /** Przerwa krótsza niż miesiąc kalendarzowy: podstawa bez zmian (art. 43). */
  'poprzednia_podstawa',
] as const
export type SickBaseKind = (typeof SICK_BASE_KINDS)[number]

/**
 * Stawka świadczenia dla przyczyny niezdolności: 100 % albo 80 % podstawy.
 * Wykaz jest zamknięty (art. 92 § 1 pkt 2 i 3 k.p., art. 11 ust. 2 ustawy
 * zasiłkowej) — gruźlica DO NIEGO NIE NALEŻY: wydłuża okres zasiłkowy do 270
 * dni, ale świadczenie zostaje na 80 %.
 */
const FULL_RATE_REASONS: readonly SickLeaveReason[] = [
  'wypadek_w_drodze',
  'ciaza',
  'dawca',
  'wypadek_przy_pracy',
]

export function sickRateIsFull(reason: SickLeaveReason): boolean {
  return FULL_RATE_REASONS.includes(reason)
}

/** Czy przyczyna oznacza zasiłek z ubezpieczenia WYPADKOWEGO od pierwszego dnia. */
export function isAccidentAtWork(reason: SickLeaveReason): boolean {
  return reason === 'wypadek_przy_pracy'
}

/**
 * Czy prawo do świadczenia przysługuje bez okresu wyczekiwania. Wypadek
 * w drodze do pracy jest wyjątkiem wprost z art. 4 ust. 3 pkt 2 ustawy
 * zasiłkowej; wypadek przy pracy — z art. 8 ust. 2 ustawy wypadkowej.
 */
export function skipsWaitingPeriod(reason: SickLeaveReason): boolean {
  return reason === 'wypadek_w_drodze' || reason === 'wypadek_przy_pracy'
}

// ---------------------------------------------------------------------------
// Kontrakty API (§7 blok SICKNESS)
// ---------------------------------------------------------------------------

/** Zwolnienie lekarskie w rejestrze kadr (spec 01 §7). */
export interface SickLeaveView {
  readonly id: string
  readonly employeeId: string
  readonly employeeName: string
  readonly employeeNo: number | null
  readonly dateFrom: string
  readonly dateTo: string
  /** Dni kalendarzowe niezdolności — świadczenie należy się za każdy dzień. */
  readonly days: number
  readonly reason: SickLeaveReason
  /** Rodzaj świadczenia: chorobowe, opiekuńcze albo macierzyńskie (Etap 21). */
  readonly benefitType: AbsenceBenefitType
  /** Nad kim opieka — tylko dla zasiłku opiekuńczego. */
  readonly careScope: CareScope | null
  /** Rodzaj urlopu — tylko dla zasiłku macierzyńskiego. */
  readonly maternityKind: MaternityLeaveKind | null
  /** Czy wniosek o oba urlopy złożono w 21 dni po porodzie (stawka 81,5 %). */
  readonly maternityCombined: boolean
  /** Numer seryjny zwolnienia z e-ZLA; `null` = kadry go nie wpisały. */
  readonly ezlaNumber: string | null
  readonly note: string | null
  /** Podstawa wymiaru w groszach — wypełniona po naliczeniu listy płac. */
  readonly baseGr: number | null
  readonly baseKind: SickBaseKind | null
  /** Czy zwolnienie leży w miesiącu z zamkniętą listą płac (nie da się usunąć). */
  readonly locked: boolean
  /**
   * Miesiące, których domknięta lista płac NIE zawiera tego zwolnienia — bo
   * wpisano je po naliczeniu (spóźnione e-ZLA). Każdy wymaga otwarcia okresu
   * ponownie i kompletu korygującego do ZUS (Etap 13).
   */
  readonly correctionMonths: readonly string[]
  readonly registeredAt: string
}

/** Dzień po dniu nie jest potrzebny — raport ZUS RSA chce zakresów. */
export interface SickAbsenceView {
  readonly kind: SickAbsenceKind
  /** Kod świadczenia/przerwy w raporcie ZUS RSA. */
  readonly zusCode: string
  readonly dateFrom: string
  readonly dateTo: string
  readonly days: number
  /** Kwota wypłacona przez płatnika; 0 gdy świadczenie wypłaca ZUS. */
  readonly amountGr: number
  /** Czy wypłaca płatnik (wynagrodzenie chorobowe zawsze, zasiłek warunkowo). */
  readonly paidByPayer: boolean
}

/**
 * Stan puli świadczeń jednej osoby (Etap 21) — liczony z zapisanych faktów,
 * nie wpisywany. Bez tego nikt nie wie, czy zasiłek jeszcze przysługuje.
 */
export interface BenefitPoolView {
  readonly employeeId: string
  readonly employeeName: string
  /** Dni okresu zasiłkowego zaliczone do bieżącego okresu (art. 8). */
  readonly benefitPeriodUsed: number
  readonly benefitPeriodLimit: number
  /** Dzień, w którym okres zasiłkowy się wyczerpuje — `null`, gdy jest zapas. */
  readonly benefitPeriodEndsOn: string | null
  /** Pierwszy dzień bieżącego okresu zasiłkowego — od niego liczymy pulę. */
  readonly benefitPeriodStartedOn: string | null
  /** Wykorzystane dni zasiłku opiekuńczego w roku, per tytuł opieki. */
  readonly careUsed: Readonly<Record<CareScope, number>>
  readonly year: number
}

export interface SickLeavesResponse {
  readonly items: readonly SickLeaveView[]
  /** Czy firma jest płatnikiem zasiłków (art. 61 ustawy zasiłkowej). */
  readonly benefitPayer: boolean
  readonly canRegister: boolean
  /** Stan puli dla osób z rejestru — okres zasiłkowy i limit opiekuńczego. */
  readonly pools: readonly BenefitPoolView[]
}
